export const ALL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const WEEKDAYS = ALL_DAYS.slice(0, 5);

export const WEEKEND_DAYS = ALL_DAYS.slice(5);

export const DEFAULT_HOURS = { start: "09:00 AM", end: "05:00 PM" };

export const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC" },
  { value: "Asia/Karachi", label: "Pakistan Standard Time (PKT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "Europe/London", label: "Greenwich Mean Time (GMT)" },
];

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
];

export function buildDefaultHours() {
  return ALL_DAYS.reduce((acc, day) => {
    acc[day] = { ...DEFAULT_HOURS };
    return acc;
  }, {});
}

export function to12Hour(time24) {
  if (!time24 || typeof time24 !== "string") return DEFAULT_HOURS.start;
  const match = time24.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return time24;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;

  return `${String(hours).padStart(2, "0")}:${minutes} ${period}`;
}

export function to24Hour(time12) {
  if (!time12 || typeof time12 !== "string") return "09:00";
  const match = time12.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    const as24 = time12.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (as24) {
      return `${String(parseInt(as24[1], 10)).padStart(2, "0")}:${as24[2]}`;
    }
    return "09:00";
  }

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;

  return `${String(hours).padStart(2, "0")}:${minutes}`;
}

export function mapApiToWorkspaceForm(data) {
  if (!data) {
    return {
      workspaceName: "",
      primaryWebsite: "",
      businessEmail: "",
      businessPhone: "",
      businessAddress: "",
      businessLogo: "",
      timezone: "UTC",
      language: "en",
      includeWeekends: false,
      hours: buildDefaultHours(),
    };
  }

  const hours = buildDefaultHours();
  ALL_DAYS.forEach((day) => {
    const entry = (data.working_hours || []).find((row) => row.day_of_week === day);
    if (entry) {
      hours[day] = {
        start: to12Hour(entry.start_time),
        end: to12Hour(entry.end_time),
      };
    }
  });

  return {
    workspaceName: data.business_name || "",
    primaryWebsite: data.business_website_link || "",
    businessEmail: data.business_email || "",
    businessPhone: data.business_phone || "",
    businessAddress: data.business_address || "",
    businessLogo: data.business_logo || "",
    timezone: data.default_timezone || "UTC",
    language: data.default_language || "en",
    includeWeekends: data.include_weekends ?? false,
    hours,
  };
}

export function mapWorkspaceFormToApi(form) {
  const working_hours = ALL_DAYS.map((day) => {
    const isWeekend = WEEKEND_DAYS.includes(day);
    const dayHours = form.hours[day] || DEFAULT_HOURS;

    return {
      day_of_week: day,
      is_available: isWeekend ? form.includeWeekends : true,
      start_time: to24Hour(dayHours.start),
      end_time: to24Hour(dayHours.end),
    };
  });

  return {
    business_name: form.workspaceName,
    business_email: form.businessEmail,
    business_phone: form.businessPhone,
    business_address: form.businessAddress,
    business_website_link: form.primaryWebsite,
    default_timezone: form.timezone,
    default_language: form.language,
    include_weekends: form.includeWeekends,
    working_hours,
  };
}

export function buildWorkspaceLogoFormData(logoFile) {
  const formData = new FormData();
  formData.append("business_logo", logoFile, logoFile.name);
  return formData;
}

export function buildWorkspaceFormData(form, logoFile) {
  const api = mapWorkspaceFormToApi(form);
  const formData = new FormData();

  formData.append("business_name", api.business_name || "");
  formData.append("business_email", api.business_email || "");
  formData.append("business_phone", api.business_phone || "");
  formData.append("business_address", api.business_address || "");
  formData.append("business_website_link", api.business_website_link || "");
  formData.append("default_timezone", api.default_timezone || "UTC");
  formData.append("default_language", api.default_language || "en");
  formData.append("include_weekends", String(api.include_weekends));
  formData.append("working_hours", JSON.stringify(api.working_hours));

  if (logoFile) {
    formData.append("business_logo", logoFile, logoFile.name);
  }

  return formData;
}

export function getErrorMessage(payload) {
  if (!payload) return "Something went wrong";
  if (typeof payload === "string") return payload;
  return (
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    "Something went wrong"
  );
}
